import { useLazyQuery } from '@apollo/client';
import { Box, IconButton, Skeleton, styled, Tooltip } from '@mui/material';
import { PartnerPlaceholderImage } from 'assets/img';
import Loader from 'Components/Forms/Loader';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import CountryRiskLevel from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import ItemSummary from 'DesignSystem/DataDisplay/ItemSummary/ItemSummary';
import SummaryBadge from 'DesignSystem/DataDisplay/SummaryBadge/SummaryBadge';
import Table from 'DesignSystem/DataDisplay/Table/Table';
import {
  IOrderBy,
  TTableColumn,
  TTableColumnComponent,
} from 'DesignSystem/DataDisplay/Table/table.types';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import { ISiteSummary } from 'graphql/secure/queries/types/site-query.types';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import React, { FC, useCallback } from 'react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import {
  ESiteType,
  IBaseFarmSite,
  IBaseSite,
  IClusterSite,
} from 'FeatureModules/Site/types/site.types';
import { getCountryRiskLevelFromCode } from 'utils/country.utils';
import { removeGraphConnections } from 'utils/graphql.utils';
import { formatAndRoundNumber } from 'utils/number.utils';
import EmptySearchResults from 'Components/EmptySearchResults';
import FlexBox from 'Components/FlexBox';
import {
  getSiteExtraInfo,
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';

export enum ESiteTableColumnKeys {
  TITLE = 'title',
  OWNER = 'ownerName',
  CREATOR = 'createdBy',
  LAST_EDITED = 'modifiedTimestamp',
  ACTIONS = 'actions',
  COLLAPSABLE = 'collapsable',
}

export type TSiteTableColumnKeys = `${ESiteTableColumnKeys}`;

interface ISiteTableProps {
  sites?: IBaseSite[];

  loading?: boolean;
  summary?: ISiteSummary;
  totalCount?: number;

  /**
   * Show the first items with only Title and Actions columns.
   */
  previewMode?: boolean;

  // TODO: Extend with Table props
  searchTerm?: string | null;
  orderBy?: IOrderBy<TSiteTableColumnKeys>;
  hasMoreRows?: boolean;
  maxHeight?: number | string;
  onOrderByChange?: (newOrderBy?: IOrderBy<TSiteTableColumnKeys>) => void;
  onEndReached?: () => void;
  onSearchTermChange?: (value: string) => void;
}

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.custom.colors.almostBlack,
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'none',
  },
}));

const TableRowSkeleton = styled(Skeleton)(({ theme }) => ({
  width: `calc(100% + 32px)`,
  height: 68,
  margin: '0 -16px',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  borderRadius: 6,
}));

const SITE_TABLE_COLUMNS = [
  {
    columnKey: ESiteTableColumnKeys.TITLE,
    headerLabel: 'Title',
    sortable: false,
    doubleWidth: true,
  },
  {
    columnKey: ESiteTableColumnKeys.OWNER,
    headerLabel: 'Owner',
    sortable: true,
  },
  {
    columnKey: ESiteTableColumnKeys.CREATOR,
    headerLabel: 'Creator',
    sortable: false,
  },
  {
    columnKey: ESiteTableColumnKeys.LAST_EDITED,
    headerLabel: 'Last edited',
    sortable: true,
  },
  { columnKey: ESiteTableColumnKeys.ACTIONS, headerLabel: 'Actions' },
  { columnKey: ESiteTableColumnKeys.COLLAPSABLE },
] satisfies TTableColumn<TSiteTableColumnKeys>[];

const CustomTableRow: FC<{
  site: IBaseSite;
  previewMode?: boolean;
  columns: TTableColumnComponent<ESiteTableColumnKeys>;
  onDelete: (site: IBaseSite) => void;
  onEdit: (site: IBaseSite) => void;
  onManageSite: (site: IBaseSite) => void;
  onEditFarmPlots: (site: IBaseFarmSite) => void;
}> = ({
  site,
  previewMode = false,
  columns: { Title, OwnerName, CreatedBy, ModifiedTimestamp, Actions, Collapsable },
  onEdit,
  onManageSite,
  onEditFarmPlots,
  onDelete,
}) => {
  const { company } = useCompanyPlanContext();
  // Retrieve full site (cluster) fragment to show the first 3 sub sites
  const [loadClusterSites, { data, loading: clusterSitesLoading }] = useLazyQuery<
    {
      site: IClusterSite;
    },
    { id: string }
  >(GET_SITE, { variables: { id: site.id } });

  const countryRiskLevel = getCountryRiskLevelFromCode(site.countryCode);

  const titleColumn = (
    <Title>
      <ItemSummary
        image={site.image}
        imagePlaceholder={
          site.siteType === ESiteType.CLUSTER ? 'site-cluster-placeholder' : 'site-placeholder'
        }
        title={getSiteTitle({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        externalId={getSiteSecondTitle({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        extraInfo={getSiteExtraInfo({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        TitleEndAdornment={countryRiskLevel && <CountryRiskLevel riskLevel={countryRiskLevel} />}
        details={[
          {
            icon: 'tag-filled',
            title:
              site.siteType === ESiteType.CLUSTER && site.sitesType
                ? `${SiteTypeLabels[site.sitesType]} cluster`
                : SiteTypeLabels[site.siteType],
          },
          site.siteType === ESiteType.CLUSTER &&
            !!site.sitesType && {
              icon: 'farm',
              title:
                `${formatAndRoundNumber(site.sitesCount, 0)} ${SiteTypeLabels[
                  site.sitesType
                ].toLowerCase()}${site.sitesCount <= 1 ? '' : 's'}` +
                (site.sitesType === ESiteType.FARM
                  ? ` - ${formatAndRoundNumber(site.plotsCount, 0)} plot${
                      site.plotsCount !== 1 ? 's' : ''
                    } (${formatAndRoundNumber(site.plotsSizeHa)} ha)`
                  : ''),
            },
          site.siteType === ESiteType.CLUSTER &&
            !!site.sitesRawMaterial && {
              icon: 'raw-material',
              title: site.sitesRawMaterial.title,
            },
          site.siteType === ESiteType.FARM &&
            !!site.rawMaterial && {
              icon: 'raw-material',
              title: site.rawMaterial.title,
            },
          site.siteType === ESiteType.FARM && {
            icon: 'farm',
            title: `${formatAndRoundNumber(site.plotsCount, 0)} plot${
              site.plotsCount !== 1 ? 's' : ''
            } (${formatAndRoundNumber(site.plotsSizeHa)} ha)`,
          },
          {
            icon: 'location',
            title: site.locationName,
          },
        ]}
      />
    </Title>
  );

  const actionsColumn = (
    <Actions>
      <Tooltip
        title={
          site.siteType === ESiteType.CLUSTER
            ? 'Manage cluster sites'
            : site.siteClusterId
            ? 'Change site cluster'
            : 'Add site to cluster'
        }
        placement="top"
      >
        <IconButton onClick={() => onManageSite(site)}>
          <Icon name="site-cluster-manage" color="gray-100" />
        </IconButton>
      </Tooltip>

      {site.siteType === ESiteType.FARM && (
        <Tooltip title="Edit cultivation area" placement="top">
          <IconButton
            data-testid="edit-site-cultivation-area"
            onClick={() => onEditFarmPlots(site)}
          >
            <Icon name="polygon-mapping" size="small" color="gray-100" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Edit" placement="top">
        <IconButton data-testid="edit-site" onClick={() => onEdit(site)}>
          <Icon name="edit" size="small" color="gray-100" />
        </IconButton>
      </Tooltip>

      <Tooltip
        title={
          site.inUse
            ? 'Site is used on an activity. Remove the site on the activity first to be able to delete.'
            : 'Delete'
        }
        placement="top"
      >
        <span>
          <IconButton
            data-testid="delete-site"
            disabled={site.inUse}
            onClick={() => onDelete(site)}
          >
            <Icon name="delete" size="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Actions>
  );

  const handleClusterExpanded = useCallback(() => {
    if (site.siteType === ESiteType.CLUSTER && site.sitesCount && !data?.site.sites) {
      loadClusterSites();
    }
  }, [site, data?.site.sites, loadClusterSites]);

  if (previewMode) {
    return (
      <>
        {titleColumn}
        {actionsColumn}
      </>
    );
  }

  return (
    <>
      {titleColumn}

      <OwnerName>
        {site.ownedBy && (
          <Box display="flex" alignItems="center">
            <Image
              width={44}
              height={44}
              image={site.ownedBy.logo}
              backupImageUrl={PartnerPlaceholderImage}
            />
            <Box ml={1}>
              <ThemeTypography variant="ITEM_SUBTITLE">{site.ownedBy.name}</ThemeTypography>
            </Box>
          </Box>
        )}
      </OwnerName>

      <CreatedBy>
        {site.createdBy && (
          <Box display="flex" alignItems="center">
            <Image
              width={44}
              height={44}
              image={site.createdBy.logo}
              backupImageUrl={PartnerPlaceholderImage}
            />
            <Box ml={1}>
              <ThemeTypography variant="ITEM_SUBTITLE">{site.createdBy.name}</ThemeTypography>
            </Box>
          </Box>
        )}
      </CreatedBy>

      <ModifiedTimestamp>
        <ThemeTypography variant="BODY_MEDIUM">
          <Moment format="MMM DD, YYYY">{site.modifiedTimestamp}</Moment>
        </ThemeTypography>
      </ModifiedTimestamp>

      {actionsColumn}

      {site.siteType === ESiteType.CLUSTER && (
        <Collapsable onExpand={handleClusterExpanded}>
          {!!site.sitesCount ? (
            <>
              {clusterSitesLoading || !data?.site.sites ? (
                <FlexBox width="100%" position="relative" height={40}>
                  <Loader size={20} />
                </FlexBox>
              ) : (
                <FlexBox flexDirection="column" alignItems="flex-start" width="100%" gap={2}>
                  <ThemeTypography variant="BODY_MEDIUM">
                    Showing 1-{data.site.sites.edges.length} of {data.site.sitesCount}.{' '}
                    <StyledLink to={`/sites/${site.id}/sites`}>
                      See all sites in cluster.
                    </StyledLink>
                  </ThemeTypography>
                  <SiteTable previewMode sites={removeGraphConnections(data.site.sites)} />
                </FlexBox>
              )}
            </>
          ) : (
            <FlexBox p={0.5}>
              <ThemeButton
                data-testid="manage-site-cluster-btn"
                color="BLUE_ICE"
                startIcon={<Icon name="site-cluster-manage" />}
                onClick={() => onManageSite(site)}
              >
                Manage cluster sites
              </ThemeButton>
            </FlexBox>
          )}
        </Collapsable>
      )}
    </>
  );
};

const SiteTable: FC<ISiteTableProps> = ({
  sites,
  summary,
  totalCount,
  loading,
  searchTerm,
  previewMode,
  ...tableProps
}) => {
  const {
    handleSiteClick,
    handleCreateSite,
    handleEditSite,
    handleDeleteSite,
    handleManageSite,
    handleEditFarmPlots,
  } = useSiteActions();

  return (
    <>
      {!previewMode && (
        <FlexBox mb={2} gap={2}>
          {loading ? (
            <>
              <Skeleton width={100} height={18} />
              <Skeleton width={60} height={30} />
              <Skeleton width={60} height={30} />
            </>
          ) : (
            <>
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                {sites?.length
                  ? `Showing 1-${sites?.length || 0} of ${totalCount}`
                  : 'No results found'}
              </ThemeTypography>

              <SummaryBadge value={summary?.farmsCount || 0} name="farm" />
              <SummaryBadge value={summary?.plotsCount || 0} name="plot" />
            </>
          )}
        </FlexBox>
      )}

      <Table
        {...tableProps}
        data-testid="sites"
        searchTerm={searchTerm}
        noScrollbarSpacing={previewMode}
        hideHeader={previewMode}
        hideTableColumns={
          previewMode
            ? [
                ESiteTableColumnKeys.OWNER,
                ESiteTableColumnKeys.CREATOR,
                ESiteTableColumnKeys.LAST_EDITED,
              ]
            : []
        }
        columns={SITE_TABLE_COLUMNS}
      >
        {({ TableRow }) => (
          <>
            {loading &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index} data-testid="site-table-row-skeleton">
                  {() => <TableRowSkeleton variant="rectangular" />}
                </TableRow>
              ))}

            {!loading &&
              !!sites?.length &&
              sites.map(site => (
                <TableRow
                  key={site.id}
                  hasCollapsable={site.siteType === ESiteType.CLUSTER}
                  onClick={() => handleSiteClick(site.id)}
                >
                  {columns => (
                    <CustomTableRow
                      key={`${site.id}-table-row-column`}
                      columns={columns}
                      site={site}
                      onDelete={handleDeleteSite}
                      onEdit={handleEditSite}
                      onManageSite={handleManageSite}
                      onEditFarmPlots={handleEditFarmPlots}
                      previewMode={previewMode}
                    />
                  )}
                </TableRow>
              ))}

            {!loading && !sites?.length && (
              <Box position="relative" height={500}>
                <EmptySearchResults
                  message="No search results found"
                  action={{
                    label: 'Add site',
                    dataTestId: 'create-site-btn',
                    onClick: handleCreateSite,
                  }}
                  skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
                />
              </Box>
            )}
          </>
        )}
      </Table>
    </>
  );
};

export default SiteTable;
